import React, { useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'

import {
  fetchCustomFields
} from 'actions/fields/fields.jsx'

import {
  Container, Modal, Button, Form, Dropdown
} from 'react-bootstrap';

import _ from 'lodash';
import cx from 'classnames';

import { EditorContext } from "components/Editor";
import { Footer, ModalHeader } from './components';

import InfiniteScroll from "components/InfiniteScroll";
import { useStoreApi } from '@xyflow/react';
import { ContentCard, ContentCardPlaceholder } from '../../../../../../components/Editor/widgets/content/sectionManager/components/contentCard';
import { AssetFilter, PropertyContext } from '../../../../../../components/AssetFilter';
import { guid } from 'services/util';
import { processRequest } from 'services/filters.service';
import { filterOptions, conditions, sortOptions, orderOptions } from 'constants/filters';
import { createJourneyStep, fetchFilterMappingsForJourney, invalidateJourneyContentManagerMappings, updateJourneyDynamicContentManager, updateJourneyStep } from 'actions/journey';

import styles from './dynamicManager.module.scss'


const DynamicManager = ({ show, journeyId, selectedNode, className, onClose }) => {
  const dispatch = useDispatch();

  const container = React.createRef(null);
  let { storyboardId } = useParams()
  const store = useStoreApi();
  const { nodes } = store.getState();
  const journeyAssetTypes = ["INTERACTIVE_CONTENT" , "LANDING_PAGE", "RESOURCE_CENTER"];
  const journeyContentId = useSelector(state => _.get(state, `journey.contentManager.id`, null)) || null;
  const steps = useSelector(state => _.get(state, `journey.${journeyId}.steps`) || []);
  const existingNode = _.some(steps, function (step) {
    return step.id === selectedNode;
  });
  const filters = useSelector(state => _.get(state, `journey.contentManager.filters`) || []);
  const sort = useSelector(state => _.get(state, `journey.contentManager.sort`) || null);
  const order = useSelector(state => _.get(state, `journey.contentManager.order`) || null);

  const contents = useSelector(state => _.get(state, `journey.contentManager.items`) || []);
  const loading = useSelector(state => _.get(state, `journey.contentManager.loading`, false));
  const pagination = useSelector(state => _.get(state, `journey.contentManager.pagination`, {}));
  const contentCount = useSelector(state => _.get(state, `journey.contentManager.pagination.totalRecordsCount`, 0));

  const filteredContents = contents?.filter(option => !journeyAssetTypes.includes(option?.asset?.storyboardType)) ; 
  const hasMoreItems = _.size(filteredContents) > 0 && _.get(pagination, "current") < _.get(pagination, "totalPages") - 1;

  // const updating = _.get(widgetProps, 'editorState.layout.updating', false);
  // const updated = _.get(widgetProps, 'editorState.layout.updated', false);

  useEffect(() => {
    dispatch(fetchCustomFields(storyboardId));

    return () => {
      dispatch(invalidateJourneyContentManagerMappings());
    }
  }, []);

  useEffect(() => {
    if (filters) {
      handleFetchMappings(0);
    }
  }, [filters, sort, order]);

  // useEffect(() => {
  //   if (updated) {
  //     handleClose();
  //   }
  // }, [updated])

  const handleFetchMappings = (page) => {
    var commandsRequest = processRequest(filters);
    commandsRequest = (commandsRequest || []).filter(item => item.operator === "IS_ALL" || item.value && item.value.length > 0);

    if (commandsRequest.length === 0) {
      var request = {
        items: []
      }

      dispatch(updateJourneyDynamicContentManager(journeyContentId, request));
      return;
    }

    var request = {
      storyboard: storyboardId,
      commands: commandsRequest,
      sort: sort,
      order: order,
    }

    dispatch(fetchFilterMappingsForJourney(journeyContentId, request, page, 20));
  }

  const loadMore = () => {
    const nextPage = pagination.current + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }

    handleFetchMappings(nextPage);
  }

  const handleSave = () => {
    var commandsRequest = processRequest(filters);
    commandsRequest = (commandsRequest || []).filter(item => item.operator === "IS_ALL" || item.value && item.value.length > 0);
    const stepDetails = _.find(nodes, (node)=>{return node.id === selectedNode});

    var contentStepRequest = {
      id: !journeyContentId ? journeyId : selectedNode,
      journey: journeyId,
      start: stepDetails && !stepDetails.parentNode ? true : "",
      mapping: contents[0].id,
      type: "CONTENT",
      commands: commandsRequest,
      sort:sort,
      order:order
    }

    if (!journeyContentId && !existingNode) {
      dispatch(createJourneyStep(journeyId, selectedNode, contentStepRequest));
    } else {
      dispatch(updateJourneyStep(journeyId, selectedNode, contentStepRequest));
    }

    handleClose()
  }

  const handleClose = () => {
    dispatch(invalidateJourneyContentManagerMappings());
    onClose();
  }

  return (
    <Modal show={show} className={styles.sectionManagerModal} backdropClassName={styles.backdrop} centered>
      <ModalHeader headerName={'Content'} type="dynamic" onClose={handleClose} />
      <Modal.Body className={styles.modalBody}>
        <Container fluid className={cx(styles.dynamicContainer, className)}>
          <section className={styles.assetGridLayout}>
            <FilterRenderer selectedNode={selectedNode} journey={journeyId} />
            <div className={styles.scrollableContainer} ref={container}>
              <InfiniteScroll
                className={cx(styles.scrollable)}
                loading={loading}
                hasMore={hasMoreItems}
                loadMore={loadMore}
              >
                {filteredContents?.map((data, index) => {
                  return (
                    <ContentCard
                      className={styles.dynamicCards}
                      key={_.get(data, 'content.id', null)}
                      assetData={data}
                    />
                  )
                })
                }
                {loading &&
                  _.times(_.size(filteredContents) == 0 ? 4 : 7).map(index => {
                    return <ContentCardPlaceholder key={index} />
                  })
                }
              </InfiniteScroll>
            </div>
          </section>
          <section className={styles.assetEditorLayout}>
          </section>
        </Container>
      </Modal.Body>
      <Modal.Footer className={styles.modalFooter}>
        <Footer type={'dynamic'} filtered={_.size(filteredContents) >0 ? filteredContents?.length : 0} selected={0} updating={false} onSave={handleSave} />
      </Modal.Footer>
    </Modal>
  )
}

const FilterRenderer = ({journey, selectedNode}) => {
  const dispatch = useDispatch();

  const journeyId = useSelector(state => _.get(state, `journey.contentManager.id`) || null);
  const filters = useSelector(state => _.get(state, `journey.contentManager.filters`) || []);
  const steps = useSelector(state => _.get(state, `journey.${journey}.steps`) || []);
  const content = _.find(steps, function (step) {
    return step.uid == selectedNode || step.id == selectedNode ;
  });

  const loading = useSelector(state => _.get(state, `journey.contentManager.loading`, false));

  const customFields = useSelector(state => _.get(state, 'fields.items', []));

  const propertiesOption = useMemo(() => {
    const customFieldProperties = customFields.map(field => {
      return {
        key: `custom_field_${field.id}`,
        value: {
          fieldId: field.id,
          type: 'CUSTOM_FIELD'
        },
        label: field.name,
        data_type: 'list',
        icon: 'list-field',
        config: {
          multiple: true,
          options: (field.options || []).map(option => {
            return {
              value: option.id,
              label: option.value
            }
          })
        }
      }
    })

    return [...filterOptions, ...customFieldProperties];
  })

  useEffect(() => {
    if (!journeyId && filters && filters.length === 0) {
      initiateLayout();
    }
  }, [])

  const initiateLayout = () => {
    var requestFilters = _.get(content, 'filters', []);

    if (!content || !content.filters || _.size(content.filters) <= 0) {
      requestFilters = [...filters, {
        property: {
          type: 'ASSET'
        },
        operator: 'IS'
      }];
    }

    requestFilters = requestFilters.map(item => {
      return {
        ...item,
        id: guid()
      }
    })

    var request = {
      ...content,
      filters: requestFilters,
      sort: _.get(content, 'sort', 'modified_date'),
      order: _.get(content, 'order', 'DESC')
    }

    dispatch(updateJourneyDynamicContentManager(journeyId, request));
  }

  const updateFilterChanges = (filterRequests) => {
    var request = {
      filters: filterRequests
    }

    dispatch(updateJourneyDynamicContentManager(journeyId, request));
  }

  const insertFilter = () => {
    var request = {
      id: guid(),
      property: {
        type: 'ASSET'
      },
      operator: 'IS'
    }

    var requestFilters = [...filters, request];

    updateFilterChanges(requestFilters);
  }

  const deleteFilter = (index) => {
    var updated = [...filters];
    updated.splice(index, 1);

    updateFilterChanges(updated);
  }

  const onFilterChange = (filter) => {
    var updated = filters.map((item, index) => {
      if (item.id === filter.id) {
        return filter;
      }

      return item;
    });

    updateFilterChanges(updated);
  }

  return (
    <Container fluid className={styles.dynamicFilterContainer}>
      <PropertyContext.Provider value={propertiesOption}>
        {filters.map((data, index) => {
          return (
            <AssetFilter
              key={index}
              index={index}
              value={data}
              onChange={onFilterChange}
              onInsert={insertFilter}
              onDelete={deleteFilter}
              canDelete={index !== 0}
              showAddFilter={index === (filters.length - 1)}
              filters={filters}
              filterFor="journey"
            />
          )
        })}
      </PropertyContext.Provider>
      <SortRender journeyId={journeyId} />
    </Container>
  )
}

const SortRender = () => {
  const dispatch = useDispatch();

  const journeyId = useSelector(state => _.get(state, `journey.contentManager.id`) || null);
  const sort = useSelector(state => _.get(state, `journey.contentManager.sort`) || {});
  const order = useSelector(state => _.get(state, `journey.contentManager.order`) || {});

  const sortOption = sortOptions.find(item => item.value === sort);
  const orderOption = orderOptions.find(item => item.value === order);

  const handleSortChange = (sort) => {
    var request = {
      sort: sort
    }
    dispatch(updateJourneyDynamicContentManager(journeyId, request));
  }

  const handleOrderChange = (order) => {
    var request = {
      order: order
    }
    dispatch(updateJourneyDynamicContentManager(journeyId, request));
  }

  const handleSortKeyDown = (event, data) => {
    if (event.key === 'Enter') {
      handleSortChange(data);
    }
  };

  const handleOrderKeyDown = (event, data) => {
    if (event.key === 'Enter') {
      handleOrderChange(data);
    }
  };

  return (
    <div className={styles.sortActionContainer}>
      <div className={styles.conditions}>
        <span className={styles.label}>
          <i className={cx(styles.sortIcon, 'cleverstory-sort')} />
          Sort
        </span>

        <Dropdown className={styles.sortDropdown} onSelect={handleSortChange}>
          <Dropdown.Toggle className={styles.selected}>
            <Button size='sm' variant='primary' className={styles.button}>
              <span className={styles.filterType}>
                {_.get(sortOption, 'label', '')}
              </span>
              <i className={cx(styles.icon, 'cleverstory-drop-down')} />
            </Button>
          </Dropdown.Toggle>
          <Dropdown.Menu className={styles.typesMenu}>
            {sortOptions.map(item => {
              return <Dropdown.Item
                eventKey={item.value}
                onKeyDown={(e) => handleSortKeyDown(e, item.value)}>
                {item.label}
              </Dropdown.Item>
            })}
          </Dropdown.Menu>
        </Dropdown>

        <Dropdown className={styles.sortDropdown} onSelect={handleOrderChange}>
          <Dropdown.Toggle className={styles.selected}>
            <Button size='sm' variant='primary' className={styles.button}>
              <span className={styles.filterType}>
                {_.get(orderOption, 'label', '')}
              </span>
              <i className={cx(styles.icon, 'cleverstory-drop-down')} />
            </Button>
          </Dropdown.Toggle>
          <Dropdown.Menu className={styles.typesMenu}>
            {orderOptions.map(item => {
              return <Dropdown.Item
                eventKey={item.value}
                onKeyDown={(e) => handleOrderKeyDown(e, item.value)}>
                {item.label}
              </Dropdown.Item>
            })}
          </Dropdown.Menu>
        </Dropdown>
      </div>
      {/* <div className={styles.actionWrapper}>
        <Button size='sm' className={styles.cancelButton} variant='secondary'>Cancel</Button>
        <Button size='sm' className={styles.saveButton} variant='primary'>Save & Close</Button>
      </div> */}
    </div>
  )
}

function getConditionsForProperty(property) {
  if (!(property && property.data_type)) {
    return [];
  }

  const filter = conditions.find(item => {
    return item.type === property.data_type;
  });

  if (!filter) {
    return [];
  }

  return filter.options || [];
}

function getDefaultConditionForProperty(property) {
  const conditions = getConditionsForProperty(property);
  if (conditions.length == 0) {
    return '';
  }

  return conditions[0].key;
}

export default DynamicManager